/**
 * Delegate Accessibility Portal
 * =============================
 * Public landing page hit by delegates after scanning the venue QR code.
 * - Detects the event from URL `/event/:eventId/accessibility`
 * - Polls /api/accessibility/events/:id/state for the active session
 * - Opens a WebSocket to /api/accessibility/ws/captions/:session/:lang
 *   and streams live captions in the chosen language
 * - Optional: per-segment TTS playback for delegates who prefer audio
 *
 * No auth required (per product spec).  Settings (chosen language, font
 * size, contrast, audio on/off) persist to localStorage so a delegate's
 * preferences survive a page refresh or QR re-scan.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Volume2, VolumeX, Type, Contrast, AccessibilityIcon, Loader2,
  Headphones,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const lsKey = (k) => `acc:${k}`;
const lsGet = (k, d) => { try { return JSON.parse(localStorage.getItem(lsKey(k))) ?? d; } catch (_e) { return d; } };
const lsSet = (k, v) => { try { localStorage.setItem(lsKey(k), JSON.stringify(v)); } catch (_e) { /* storage blocked */ } };

const DelegateAccessibility = () => {
  const { eventId } = useParams();

  const [state, setState] = useState(null);         // /events/:id/state response
  const [error, setError] = useState(null);
  const [lang, setLang] = useState(() => lsGet('lang', 'en'));
  const [fontSize, setFontSize] = useState(() => lsGet('font', 24));
  const [contrast, setContrast] = useState(() => lsGet('contrast', 'normal'));
  const [audioOn, setAudioOn] = useState(() => lsGet('audio', false));
  const [segments, setSegments] = useState([]);
  const [wsStatus, setWsStatus] = useState('idle'); // idle | connecting | open | closed
  const [languageCatalog, setLanguageCatalog] = useState([]);

  const wsRef = useRef(null);
  const captionsEndRef = useRef(null);
  const audioQueueRef = useRef([]);
  const audioPlayingRef = useRef(false);

  // ---- Persist user prefs ----
  useEffect(() => { lsSet('lang', lang); }, [lang]);
  useEffect(() => { lsSet('font', fontSize); }, [fontSize]);
  useEffect(() => { lsSet('contrast', contrast); }, [contrast]);
  useEffect(() => { lsSet('audio', audioOn); }, [audioOn]);

  // ---- Fetch state + language catalog ----
  useEffect(() => {
    let alive = true;
    const fetchState = async () => {
      try {
        const r = await fetch(`${API}/api/accessibility/events/${eventId}/state`);
        if (!r.ok) throw new Error(`State fetch failed (${r.status})`);
        const data = await r.json();
        if (!alive) return;
        setState(data);
        // First load — adopt event defaults if user hasn't overridden.
        if (lsGet('font', null) === null) setFontSize(data.font_size_default || 24);
        if (lsGet('contrast', null) === null) setContrast(data.contrast_mode_default || 'normal');
      } catch (e) {
        if (alive) setError(e.message);
      }
    };
    fetchState();
    const t = setInterval(fetchState, 10000); // re-poll so we notice when admin starts/stops a session
    fetch(`${API}/api/accessibility/languages`).then(r => r.json()).then(d => setLanguageCatalog(d.languages || [])).catch(() => {});
    return () => { alive = false; clearInterval(t); };
  }, [eventId]);

  const activeSession = state?.active_session;

  // ---- WebSocket subscription ----
  useEffect(() => {
    if (!activeSession?.id || !state?.enabled) return;
    // Tear down previous socket whenever session id or chosen lang changes.
    const wsUrl = `${API.replace(/^http/, 'ws')}/api/accessibility/ws/captions/${activeSession.id}/${lang}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    setWsStatus('connecting');
    setSegments([]);

    ws.onopen = () => setWsStatus('open');
    ws.onclose = () => setWsStatus('closed');
    ws.onerror = () => setWsStatus('closed');
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === 'segment' && msg.text) {
          setSegments((prev) => {
            // de-dupe by id, keep last 200 for memory safety
            if (prev.some(s => s.id === msg.id)) return prev;
            const next = [...prev, msg];
            return next.slice(-200);
          });
          if (audioOn) enqueueAudio(msg.text);
        }
      } catch (_e) { /* malformed frame */ }
    };

    return () => {
      try { ws.close(); } catch (_e) { /* already closed */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession?.id, lang, state?.enabled]);

  // ---- Auto-scroll captions ----
  useEffect(() => {
    captionsEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [segments]);

  // ---- TTS queue (only when delegate enabled audio) ----
  const enqueueAudio = (text) => {
    audioQueueRef.current.push(text);
    if (!audioPlayingRef.current) playNext();
  };
  const playNext = async () => {
    if (audioQueueRef.current.length === 0) {
      audioPlayingRef.current = false;
      return;
    }
    audioPlayingRef.current = true;
    const text = audioQueueRef.current.shift();
    try {
      const url = `${API}/api/accessibility/tts?text=${encodeURIComponent(text.slice(0, 600))}&lang=${lang}`;
      const audio = new Audio(url);
      audio.onended = () => { audioPlayingRef.current = false; playNext(); };
      audio.onerror = () => { audioPlayingRef.current = false; playNext(); };
      await audio.play();
    } catch (_e) {
      audioPlayingRef.current = false;
      playNext();
    }
  };

  // ---- Render gates ----
  const containerClass = useMemo(() => {
    if (contrast === 'high') return 'min-h-screen bg-black text-yellow-300';
    return 'min-h-screen bg-slate-950 text-white';
  }, [contrast]);

  if (error && !state) {
    return (
      <div className={containerClass}>
        <div className="max-w-md mx-auto p-6">
          <Card><CardContent className="p-6">
            <p className="text-red-400" data-testid="accessibility-error">{error}</p>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className={containerClass}>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  if (!state.enabled) {
    return (
      <div className={containerClass}>
        <div className="max-w-md mx-auto p-6">
          <Card><CardContent className="p-8 text-center space-y-3">
            <AccessibilityIcon className="h-10 w-10 mx-auto text-slate-400" />
            <h1 className="text-xl font-bold">{state.event_name}</h1>
            <p className="text-slate-400 text-sm" data-testid="accessibility-disabled">
              Live captions are not enabled for this event.
            </p>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  const allowedLangs = (state.languages || []).map(code => languageCatalog.find(l => l.code === code) || { code, name: code, native: code });

  return (
    <div className={containerClass} data-testid="delegate-accessibility">
      <div className="max-w-3xl mx-auto p-3 sm:p-6 space-y-4">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-lg font-bold truncate">{state.event_name}</h1>
            <p className="text-xs text-slate-400">Accessibility & live captions</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={activeSession ? 'default' : 'secondary'}
                   className={activeSession ? 'bg-emerald-600' : ''}
                   data-testid="accessibility-session-status">
              {activeSession ? `Live — ${activeSession.label || 'session'}` : 'Standby'}
            </Badge>
            <Badge variant="outline" className="text-[10px]" data-testid="accessibility-ws-status">{wsStatus}</Badge>
            <Button asChild size="sm" variant="ghost" className="text-slate-300 hover:text-white">
              <a href={`/event/${eventId}/accessibility/library`} data-testid="accessibility-library-link">
                Library
              </a>
            </Button>
          </div>
        </header>

        {/* Controls */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase text-slate-400 flex items-center gap-1 mb-1">
                <AccessibilityIcon className="h-3 w-3" /> Language
              </label>
              <Select value={lang} onValueChange={setLang}>
                <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="accessibility-lang-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allowedLangs.map(l => (
                    <SelectItem key={l.code} value={l.code} data-testid={`accessibility-lang-option-${l.code}`}>
                      {l.native} — {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase text-slate-400 flex items-center gap-1 mb-1">
                <Type className="h-3 w-3" /> Font size · {fontSize}px
              </label>
              <Slider min={14} max={56} step={2} value={[fontSize]} onValueChange={(v) => setFontSize(v[0])}
                      data-testid="accessibility-font-slider" />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase text-slate-400 flex items-center gap-1">
                <Contrast className="h-3 w-3" /> High contrast
              </label>
              <Switch checked={contrast === 'high'} onCheckedChange={(v) => setContrast(v ? 'high' : 'normal')}
                      data-testid="accessibility-contrast-switch" />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase text-slate-400 flex items-center gap-1">
                {audioOn ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />} Spoken audio
              </label>
              <Switch checked={audioOn} onCheckedChange={setAudioOn} disabled={!state.tts_enabled}
                      data-testid="accessibility-audio-switch" />
            </div>
          </CardContent>
        </Card>

        {/* Live caption stream */}
        <Card className={contrast === 'high' ? 'bg-black border-yellow-500' : 'bg-slate-900/60 border-slate-800'}>
          <CardContent className="p-4">
            {!activeSession ? (
              <div className="py-12 text-center text-slate-400 text-sm" data-testid="accessibility-no-session">
                Waiting for the speaker to start the session…
              </div>
            ) : (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="accessibility-stream">
                {segments.length === 0 ? (
                  <p className="text-slate-500 text-sm py-6 text-center">Captions will appear here as the speaker speaks…</p>
                ) : segments.map(s => (
                  <p key={s.id} className="leading-relaxed" style={{ fontSize: `${fontSize}px` }}
                     data-testid={`accessibility-segment-${s.id}`}>
                    {s.text}
                  </p>
                ))}
                <div ref={captionsEndRef} />
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-[10px] text-slate-500 text-center flex items-center justify-center gap-1">
          <Headphones className="h-3 w-3" /> Captions are generated in real time and may contain minor errors.
        </p>
      </div>
    </div>
  );
};

export default DelegateAccessibility;
